/**
 * BreakGlassPage — listing dedicat pentru credentialele marcate `is_break_glass`.
 * Warning banner rose, acces separat de vault-ul normal; fiecare acces
 * generează un audit event special (credential.break_glass_access).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import { credentialsApi, type CredentialSummary } from '@/api/credentials';
import { formatDate } from '@/lib/format';
import { useAuthStore } from '@/stores/auth';
import VaultDetailModal from '@/features/vault/VaultDetail';
import InfoTooltip from '@/components/InfoTooltip';

export default function BreakGlassPage() {
  const { t } = useTranslation();
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const keyPair = useAuthStore((s) => s.keyPair);
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['break-glass', tenantId],
    queryFn: () => credentialsApi.listBreakGlass(tenantId!),
    enabled: !!tenantId,
  });

  if (!tenantId || !keyPair) {
    return (
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        {t('vault.no_tenant')}
      </p>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="display-title flex items-center gap-2">
          {t('nav.break_glass')}
          <InfoTooltip text={t('break_glass.info_tooltip')} width={320} />
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
          {t('admin_ui.break_glass.subtitle')}
        </p>
      </div>

      {/* Warning banner */}
      <div
        className="rounded-lg p-4 mb-6 flex items-start gap-3"
        style={{
          background: 'var(--bg-rose)',
          border: '1px solid var(--c-rose)',
          color: 'var(--c-rose)',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="flex-shrink-0 mt-0.5"
          aria-hidden
        >
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <div className="text-[13px] leading-relaxed">
          <strong className="block mb-1">{t('admin_ui.break_glass.warning_title')}</strong>
          <Trans
            i18nKey="admin_ui.break_glass.warning_body"
            components={{ code: <code className="font-mono text-[11px]" /> }}
          />
        </div>
      </div>

      {list.isLoading && (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('common.loading')}
        </p>
      )}

      {list.data && list.data.length === 0 && (
        <div
          className="rounded-lg p-8 text-center"
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {t('admin_ui.break_glass.empty')}
          </p>
          <p
            className="text-[11px] mt-2 font-mono"
            style={{ color: 'var(--text-dim)' }}
          >
            {t('admin_ui.break_glass.empty_hint')}
          </p>
        </div>
      )}

      {list.data && list.data.length > 0 && (
        <div className="space-y-2">
          {list.data.map((c) => (
            <BreakGlassRow key={c.id} cred={c} onOpen={() => setOpenId(c.id)} />
          ))}
        </div>
      )}

      {openId && (
        <VaultDetailModal
          id={openId}
          onClose={() => setOpenId(null)}
          keyPair={keyPair}
        />
      )}
    </div>
  );
}

function BreakGlassRow({
  cred,
  onOpen,
}: {
  cred: CredentialSummary;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-lg p-4 text-left transition-colors"
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderLeft: '3px solid var(--c-rose)',
        color: 'var(--text)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--bg-elevated)';
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium">{cred.title}</div>
          <div
            className="text-[11px] mt-0.5 font-mono flex items-center gap-2"
            style={{ color: 'var(--text-dim)' }}
          >
            <span>{cred.type}</span>
            <span>·</span>
            <span>
              {t('admin_ui.break_glass.updated', { when: formatDate(cred.updated_at) })}
            </span>
            {cred.access_count > 0 && (
              <>
                <span>·</span>
                <span>{t('admin_ui.break_glass.access_count', { count: cred.access_count })}</span>
              </>
            )}
          </div>
        </div>
        <span
          className="font-mono text-[10px] px-2 py-1 rounded-full flex-shrink-0"
          style={{
            color: 'var(--c-rose)',
            background: 'var(--bg-rose)',
            border: '1px solid var(--c-rose)',
            letterSpacing: '0.08em',
          }}
        >
          {t('admin_ui.break_glass.badge')}
        </span>
      </div>
    </button>
  );
}
